import { Badge } from '@/components/ui/badge';
import type { JourneyStep } from '../types';

interface JourneyTimelineProps {
  steps: JourneyStep[];
}

/**
 * Horizontal timeline for a journey's step cadence, purely from
 * step.delayMinutes (the delay between step N-1 and step N).
 * Cumulative offsets are rendered above each node so admins can
 * answer "when does Step 3 actually fire?" at a glance.
 */
export function JourneyTimeline({ steps }: JourneyTimelineProps) {
  if (steps.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">No steps defined.</p>
    );
  }

  // Compute cumulative delay from entry for each step.
  let cumulative = 0;
  const withOffsets = steps.map((step) => {
    cumulative += step.delayMinutes;
    return { step, offsetMinutes: cumulative };
  });

  return (
    <div className="w-full overflow-x-auto">
      <div
        className="relative grid items-start gap-3 pt-8 pb-2"
        style={{
          gridTemplateColumns: `repeat(${withOffsets.length + 1}, minmax(120px, 1fr))`,
        }}
      >
        {/* Track */}
        <div
          className="absolute left-0 right-0 top-[40px] h-0.5 bg-border"
          aria-hidden
        />

        {/* Entry node */}
        <div className="relative flex flex-col items-center">
          <span className="absolute -top-6 text-[10px] uppercase tracking-wide text-muted-foreground">
            Entry
          </span>
          <span className="z-10 inline-flex h-4 w-4 items-center justify-center rounded-full border-2 border-primary bg-background" />
          <span className="mt-2 text-[10px] text-muted-foreground">0m</span>
        </div>

        {withOffsets.map(({ step, offsetMinutes }, i) => {
          const delta = step.delayMinutes;
          return (
            <div
              key={step.stepNumber}
              className="relative flex flex-col items-center"
              style={{ minWidth: 120 }}
            >
              <span className="absolute -top-6 text-[10px] uppercase tracking-wide text-muted-foreground">
                {humanizeRelative(delta)}
                {i === 0 ? ' after entry' : ''}
              </span>
              <span
                className={`z-10 inline-flex h-4 w-4 items-center justify-center rounded-full border-2 bg-background ${
                  step.isEscalation ? 'border-destructive' : 'border-primary'
                }`}
              />
              <div className="mt-2 flex flex-col items-center gap-1 text-center">
                <div className="text-xs font-medium">
                  Step {step.stepNumber}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  cumulative {humanizeAbsolute(offsetMinutes)}
                </div>
                <Badge
                  variant="outline"
                  className="text-[10px] font-normal"
                  title={`${step.category} · ${step.channel} · ${step.priority}`}
                >
                  {step.category}
                </Badge>
                {step.isUrgent && <Badge variant="warning">Urgent</Badge>}
                {step.isEscalation && <Badge variant="destructive">Escalation</Badge>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function humanizeRelative(minutes: number): string {
  if (minutes <= 0) return 'immediately';
  if (minutes < 60) return `+${minutes}m`;
  const hours = minutes / 60;
  if (hours < 24) {
    const h = Math.floor(hours);
    const m = minutes - h * 60;
    return m > 0 ? `+${h}h ${m}m` : `+${h}h`;
  }
  const days = Math.floor(minutes / 1440);
  const remHours = Math.floor((minutes - days * 1440) / 60);
  return remHours > 0 ? `+${days}d ${remHours}h` : `+${days}d`;
}

function humanizeAbsolute(minutes: number): string {
  if (minutes === 0) return '0m';
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) {
    const h = Math.floor(minutes / 60);
    const m = minutes - h * 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const days = Math.floor(minutes / 1440);
  const remHours = Math.floor((minutes - days * 1440) / 60);
  return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
}
